import { clamp, preventDefault } from './utilities.js';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const DEFAULT_ZOOM = 1;
const ZOOM_STEP = 1.25;
const DRAG_THRESHOLD = 3;

export class DragZoomWrapper extends HTMLElement {
  #controller = new AbortController();
  /** @type {number} */
  #scale = DEFAULT_ZOOM;
  /** @type {number} */
  #translateX = 0;
  /** @type {number} */
  #translateY = 0;
  /** @type {number} */
  #initialDistance = 0;
  /** @type {number} */
  #startScale = DEFAULT_ZOOM;
  /** @type {boolean} */
  #initialized = false;
  /** @type {number | null} */
  #animationFrame = null;
  /** @type {boolean} */
  #isDragging = false;
  /** @type {boolean} */
  #didDrag = false;
  /** @type {number} */
  #startX = 0;
  /** @type {number} */
  #startY = 0;
  /** @type {number} */
  #startTranslateX = 0;
  /** @type {number} */
  #startTranslateY = 0;
  /** @type {number | null} */
  #activePointerId = null;
  /** @type {number} */
  #lastEmittedScale = DEFAULT_ZOOM;

  get #image() {
    return this.querySelector('img');
  }

  get isAtMinZoom() {
    return this.#scale <= MIN_ZOOM + 0.001;
  }

  get isAtMaxZoom() {
    return this.#scale >= MAX_ZOOM - 0.001;
  }

  connectedCallback() {
    if (!this.#image) return;

    this.#resizeObserver.observe(this);
    this.#initEventListeners();
    this.#updateTransform();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
  }

  #initEventListeners() {
    if (this.#initialized) return;
    this.#initialized = true;

    const { signal } = this.#controller;
    const passiveFalse = { passive: false, signal };

    this.addEventListener('wheel', this.#handleWheel, passiveFalse);
    this.addEventListener('touchstart', this.#handleTouchStart, passiveFalse);
    this.addEventListener('touchmove', this.#handleTouchMove, passiveFalse);
    this.addEventListener('touchend', this.#handleTouchEnd, { signal });
    this.addEventListener('touchcancel', this.#handleTouchEnd, { signal });
    this.addEventListener('pointerdown', this.#handlePointerDown, { signal });
    this.addEventListener('pointermove', this.#handlePointerMove, passiveFalse);
    this.addEventListener('pointerup', this.#handlePointerUp, { signal });
    this.addEventListener('pointercancel', this.#handlePointerUp, { signal });
    this.addEventListener('click', this.#handleClick, { capture: true, signal });
    this.addEventListener('dragstart', preventDefault, { signal });
    this.addEventListener('load', this.#handleImageLoad, { capture: true, signal });
  }

  #resizeObserver = new ResizeObserver(() => {
    if (!this.#initialized) {
      this.#initEventListeners();
    }
    if (this.#initialized) {
      this.#requestUpdateTransform();
    }
  });

  #handleImageLoad = () => {
    if (this.#image) this.#image.draggable = false;
    this.#requestUpdateTransform();
  };

  #isEventOnImage(event) {
    const image = this.#image;
    if (!image || !(event.target instanceof Node)) return false;
    return event.target === image || image.contains(event.target);
  }

  #beginDrag(x, y) {
    this.#isDragging = true;
    this.#didDrag = false;
    this.#startX = x;
    this.#startY = y;
    this.#startTranslateX = this.#translateX;
    this.#startTranslateY = this.#translateY;
    this.classList.add('is-dragging');
  }

  #moveDrag(x, y) {
    const dx = x - this.#startX;
    const dy = y - this.#startY;

    if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) {
      this.#didDrag = true;
    }

    this.#translateX = this.#startTranslateX + dx;
    this.#translateY = this.#startTranslateY + dy;
    this.#requestUpdateTransform();
  }

  #endDrag() {
    if (!this.#isDragging) return;

    this.#isDragging = false;
    this.#activePointerId = null;
    this.classList.remove('is-dragging');
  }

  #handleWheel = (event) => {
    preventDefault(event);

    const factor = event.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP;
    this.#scale = clamp(this.#scale * factor, MIN_ZOOM, MAX_ZOOM);
    this.#requestUpdateTransform();
  };

  #handlePointerDown = (event) => {
    if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
    if (event.button !== 0) return;
    if (this.isAtMinZoom || !this.#isEventOnImage(event)) return;

    preventDefault(event);
    this.#activePointerId = event.pointerId;
    this.setPointerCapture(event.pointerId);
    this.#beginDrag(event.clientX, event.clientY);
  };

  #handlePointerMove = (event) => {
    if (!this.#isDragging || event.pointerId !== this.#activePointerId) return;

    preventDefault(event);
    this.#moveDrag(event.clientX, event.clientY);
  };

  #handlePointerUp = (event) => {
    if (event.pointerId !== this.#activePointerId) return;

    if (this.hasPointerCapture(event.pointerId)) {
      this.releasePointerCapture(event.pointerId);
    }

    this.#endDrag();
  };

  #handleClick = (event) => {
    if (!this.#didDrag) return;

    preventDefault(event);
    event.stopPropagation();
    this.#didDrag = false;
  };

  #handleTouchStart = (event) => {
    if (event.touches.length === 2) {
      this.#endDrag();
      preventDefault(event);

      const [point1, point2] = Array.from(event.touches).map(touchToPoint);
      if (!point1 || !point2) return;

      this.#initialDistance = getDistance(point1, point2);
      this.#startScale = this.#scale;
      return;
    }

    if (event.touches.length !== 1 || this.isAtMinZoom || !this.#isEventOnImage(event)) return;

    const touch = event.touches[0];
    if (!touch) return;

    this.#beginDrag(touch.clientX, touch.clientY);
  };

  #handleTouchMove = (event) => {
    if (event.touches.length === 2) {
      preventDefault(event);

      const [point1, point2] = Array.from(event.touches).map(touchToPoint);
      if (!point1 || !point2) return;

      const currentDistance = getDistance(point1, point2);
      const newScale = (currentDistance / this.#initialDistance) * this.#startScale;
      this.#scale = clamp(newScale, MIN_ZOOM, MAX_ZOOM);
      this.#requestUpdateTransform();
      return;
    }

    if (event.touches.length !== 1 || !this.#isDragging) return;

    preventDefault(event);
    const touch = event.touches[0];
    if (!touch) return;

    this.#moveDrag(touch.clientX, touch.clientY);
  };

  #handleTouchEnd = (event) => {
    if (event.touches.length === 0) {
      this.#endDrag();
      return;
    }

    if (event.touches.length === 1 && !this.isAtMinZoom) {
      const touch = event.touches[0];
      if (!touch) return;
      this.#beginDrag(touch.clientX, touch.clientY);
    }
  };

  #clampTranslate() {
    const image = this.#image;

    if (!image || this.isAtMinZoom) {
      this.#translateX = 0;
      this.#translateY = 0;
      return;
    }

    const scaledWidth = image.offsetWidth * this.#scale;
    const scaledHeight = image.offsetHeight * this.#scale;
    const maxX = scaledWidth / 2;
    const maxY = scaledHeight / 2;

    this.#translateX = clamp(this.#translateX, -maxX, maxX);
    this.#translateY = clamp(this.#translateY, -maxY, maxY);
  }

  #requestUpdateTransform() {
    if (!this.#animationFrame) {
      this.#animationFrame = requestAnimationFrame(this.#updateTransform);
    }
  }

  #cancelAnimationFrame() {
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }
  }

  #updateTransform = () => {
    this.#animationFrame = null;
    this.#scale = clamp(this.#scale, MIN_ZOOM, MAX_ZOOM);
    this.#clampTranslate();

    if (this.#image) this.#image.draggable = false;

    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translateX}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translateY}px`);
    this.classList.toggle('is-zoomable', !this.isAtMinZoom);

    if (this.#lastEmittedScale === this.#scale) return;

    this.#lastEmittedScale = this.#scale;
    this.dispatchEvent(
      new CustomEvent('zoom-scale-change', {
        bubbles: true,
        detail: { atMinZoom: this.isAtMinZoom, atMaxZoom: this.isAtMaxZoom },
      })
    );
  };

  reset() {
    this.#scale = DEFAULT_ZOOM;
    this.#translateX = 0;
    this.#translateY = 0;
    this.#endDrag();
    this.#requestUpdateTransform();
  }

  zoomIn() {
    this.#scale = clamp(this.#scale * ZOOM_STEP, MIN_ZOOM, MAX_ZOOM);
    this.#requestUpdateTransform();
  }

  zoomOut() {
    this.#scale = clamp(this.#scale / ZOOM_STEP, MIN_ZOOM, MAX_ZOOM);
    this.#requestUpdateTransform();
  }

  destroy() {
    this.#controller.abort();
    this.#cancelAnimationFrame();
  }
}

if (!customElements.get('drag-zoom-wrapper')) {
  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}

/**
 * @typedef {Object} Point
 * @property {number} x
 * @property {number} y
 */

/**
 * @param {Touch | undefined} touch
 * @returns {Point | undefined}
 */
function touchToPoint(touch) {
  if (!touch) return undefined;
  return { x: touch.clientX, y: touch.clientY };
}

/**
 * @param {Point} point1
 * @param {Point} point2
 * @returns {number}
 */
function getDistance(point1, point2) {
  const dx = point1.x - point2.x;
  const dy = point1.y - point2.y;
  return Math.sqrt(dx * dx + dy * dy);
}
